/**
 * @module components/Browse
 * @description Brief §4.3: keyword search; category, parish and urgency
 * filters; theme tags; sort; grid/list; clear filters; live result counter;
 * Load More; sticky filters. Plus "Saved" (brief §4.4 save/favorite).
 *
 * State (the query) is owned by the page and passed in, so the hero search
 * and this toolbar drive the same results.
 */
import { useEffect, useMemo, useState, type JSX } from "react";
import { useGiving } from "../app/GivingContext";
import { PAGE_SIZE } from "../config/page";
import { CATEGORY_LABELS, PARISH_LABELS, URGENCY_LABELS } from "../config/taxonomy";
import { allTags, applyQuery, countBy, EMPTY_QUERY, hasActiveFilters, toggle } from "../domain/query";
import type { Cause, CauseQuery, CategoryId, ParishId, SortId, UrgencyId } from "../domain/types";
import { plural } from "../lib/format";
import { CauseCard } from "./CauseCard";
import { Icon } from "./Icons";
import "./Browse.css";

/** Sort labels, in the brief's order. */
const SORT_LABELS: Readonly<Record<SortId, string>> = {
    popular: "Most Popular",
    urgent: "Most Urgent",
    newest: "Newest",
    updated: "Recently Updated",
};

/** Props for <Browse>. */
interface BrowseProps {
    causes: Cause[];
    query: CauseQuery;
    onQuery: (query: CauseQuery) => void;
}

/** The browse section: sticky toolbar + results. */
export function Browse({ causes, query, onQuery }: BrowseProps): JSX.Element {
    const { saved } = useGiving();
    const [layout, setLayout] = useState<"grid" | "list">("grid");
    const [visible, setVisible] = useState<number>(PAGE_SIZE);
    const [savedOnly, setSavedOnly] = useState<boolean>(false);
    const [panelOpen, setPanelOpen] = useState<boolean>(false);

    const results: Cause[] = useMemo(() => {
        const matched: Cause[] = applyQuery(causes, query);
        return savedOnly ? matched.filter((c: Cause) => saved.includes(c.id)) : matched;
    }, [causes, query, savedOnly, saved]);

    // A new search/filter starts from the first page again.
    useEffect(() => setVisible(PAGE_SIZE), [query, savedOnly]);

    const categoryCounts = useMemo(() => countBy<CategoryId>(causes, (c) => c.category), [causes]);
    const urgencyCounts = useMemo(() => countBy<UrgencyId>(causes, (c) => c.urgency), [causes]);
    const parishCounts = useMemo(() => countBy<ParishId>(causes, (c) => c.parish), [causes]);
    const tags: string[] = useMemo(() => allTags(causes).slice(0, 10), [causes]);
    const active: boolean = hasActiveFilters(query) || savedOnly;
    const activeCount: number = query.categories.length + query.parishes.length + query.urgencies.length + query.tags.length + (savedOnly ? 1 : 0);

    /** Shallow-merge a change into the query. */
    const set = (patch: Partial<CauseQuery>): void => onQuery({ ...query, ...patch });
    const clear = (): void => {
        onQuery({ ...EMPTY_QUERY, sort: query.sort });
        setSavedOnly(false);
    };

    const shown: Cause[] = results.slice(0, visible);

    return (
        <section id="browse" className="section browse" aria-labelledby="browse-title">
            <div className="container">
                <div className="section__head">
                    <div>
                        <p className="kicker">All causes <span className="badge badge--sample">Sample causes</span></p>
                        <h2 id="browse-title" className="section__title">Find your cause</h2>
                    </div>
                </div>
            </div>

            <div className={`browse__toolbar${panelOpen ? " is-open" : ""}`}>
                <div className="container">
                    <div className="browse__row">
                        <label className="browse__search">
                            <Icon name="search" />
                            <span className="visually-hidden">Search causes</span>
                            <input type="search" value={query.text} placeholder="Search causes, partners, places" onChange={(e) => set({ text: e.target.value })} />
                        </label>
                        <label className="browse__sort">
                            <span className="visually-hidden">Sort by</span>
                            <select className="select" value={query.sort} onChange={(e) => set({ sort: e.target.value as SortId })}>
                                {(Object.keys(SORT_LABELS) as SortId[]).map((id: SortId) => <option key={id} value={id}>{SORT_LABELS[id]}</option>)}
                            </select>
                        </label>
                        <div className="browse__view" role="group" aria-label="Layout">
                            <button type="button" className="icon-btn" aria-pressed={layout === "grid"} onClick={() => setLayout("grid")} aria-label="Grid view"><Icon name="grid" size={18} /></button>
                            <button type="button" className="icon-btn" aria-pressed={layout === "list"} onClick={() => setLayout("list")} aria-label="List view"><Icon name="list" size={18} /></button>
                        </div>
                        <button type="button" className="btn btn--ghost browse__filters-toggle" aria-expanded={panelOpen} onClick={() => setPanelOpen((o: boolean) => !o)}>
                            Filters{activeCount > 0 ? ` (${activeCount})` : ""}
                        </button>
                    </div>

                    <div className="browse__panel">
                        <div className="browse__group" role="group" aria-label="Category">
                            {(Object.keys(CATEGORY_LABELS) as CategoryId[]).map((id: CategoryId) => {
                                const n: number = categoryCounts.get(id) ?? 0;
                                return (
                                    <button key={id} type="button" className="chip" aria-pressed={query.categories.includes(id)} disabled={n === 0}
                                        onClick={() => set({ categories: toggle(query.categories, id) })}>
                                        {CATEGORY_LABELS[id]} <span className="chip__count">{n}</span>
                                    </button>
                                );
                            })}
                        </div>
                        <div className="browse__group" role="group" aria-label="Urgency and location">
                            {(Object.keys(URGENCY_LABELS) as UrgencyId[]).map((id: UrgencyId) => (
                                <button key={id} type="button" className="chip" aria-pressed={query.urgencies.includes(id)}
                                    onClick={() => set({ urgencies: toggle(query.urgencies, id) })}>
                                    {URGENCY_LABELS[id]} <span className="chip__count">{urgencyCounts.get(id) ?? 0}</span>
                                </button>
                            ))}
                            <label className="browse__parish">
                                <span className="visually-hidden">Parish</span>
                                <select className="select" value={query.parishes[0] ?? ""} onChange={(e) => set({ parishes: e.target.value === "" ? [] : [e.target.value as ParishId] })}>
                                    <option value="">All parishes</option>
                                    {(Object.keys(PARISH_LABELS) as ParishId[]).filter((id) => parishCounts.has(id)).map((id: ParishId) => (
                                        <option key={id} value={id}>{PARISH_LABELS[id]} ({parishCounts.get(id)})</option>
                                    ))}
                                </select>
                            </label>
                            <button type="button" className="chip" aria-pressed={savedOnly} onClick={() => setSavedOnly((s: boolean) => !s)}>
                                <Icon name={savedOnly ? "heart-filled" : "heart"} size={14} /> Saved <span className="chip__count">{saved.length}</span>
                            </button>
                        </div>
                        <div className="browse__group browse__tags" role="group" aria-label="Themes">
                            {tags.map((tag: string) => (
                                <button key={tag} type="button" className="chip chip--tag" aria-pressed={query.tags.includes(tag)} onClick={() => set({ tags: toggle(query.tags, tag) })}>#{tag}</button>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            <div className="container">
                <div className="browse__status">
                    <p aria-live="polite">Showing <strong>{Math.min(visible, results.length)}</strong> of {plural(results.length, "cause")}</p>
                    {active && <button type="button" className="browse__clear" onClick={clear}><Icon name="close" size={14} /> Clear filters</button>}
                </div>

                {results.length === 0 ? (
                    <div className="browse__empty">
                        <h3>No causes match yet</h3>
                        <p>Try fewer filters or a different word.</p>
                        <button type="button" className="btn btn--primary" onClick={clear}>Clear filters</button>
                    </div>
                ) : (
                    <div className={`browse__results browse__results--${layout}`}>
                        {shown.map((cause: Cause, i: number) => <CauseCard key={cause.id} cause={cause} layout={layout} index={i} />)}
                    </div>
                )}

                {visible < results.length && (
                    <div className="browse__more">
                        <button type="button" className="btn btn--ghost btn--lg" onClick={() => setVisible((v: number) => v + PAGE_SIZE)}>
                            Load more causes ({results.length - visible} more)
                        </button>
                    </div>
                )}
            </div>
        </section>
    );
}
